import React, { useEffect, useState } from 'react';
import { AiOutlineEdit } from 'react-icons/ai';
import Button from './common/Button';
import Input from './common/Input';

interface EditableNameProps {
  firstName: string;
  lastName: string;
  onSave: (firstName: string, lastName: string) => Promise<void>;
  className?: string;
}

const EditableName: React.FC<EditableNameProps> = ({
  firstName: initialFirstName,
  lastName: initialLastName,
  onSave,
  className = '',
}) => {
  const [editing, setEditing] = useState(false);
  const [firstName, setFirstName] = useState(initialFirstName);
  const [lastName, setLastName] = useState(initialLastName);
  const [changed, setChanged] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    setFirstName(initialFirstName);
    setLastName(initialLastName);
    setChanged(false);
    setEditing(false);
  }, [initialFirstName, initialLastName]);

  useEffect(() => {
    setChanged(firstName !== initialFirstName || lastName !== initialLastName);
  }, [firstName, lastName, initialFirstName, initialLastName]);

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      await onSave(firstName, lastName);
      setChanged(false);
      setEditing(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save changes.');
    } finally {
      setSaving(false);
    }
  };

  if (editing) {
    return (
      <div className={className}>
        <div className="grid gap-3 sm:grid-cols-2">
          <Input value={firstName} onChange={(e) => setFirstName(e.target.value)} label="First name" />
          <Input value={lastName} onChange={(e) => setLastName(e.target.value)} label="Last name" />
        </div>
        <div className="mt-4 flex flex-wrap gap-3">
          <Button onClick={handleSave} disabled={!changed || saving} isLoading={saving}>
            Save name
          </Button>
          <Button
            variant="secondary"
            onClick={() => {
              setFirstName(initialFirstName);
              setLastName(initialLastName);
              setEditing(false);
              setChanged(false);
            }}
            disabled={saving}
          >
            Cancel
          </Button>
        </div>
        {error && <div className="mt-3 text-sm text-red-600">{error}</div>}
      </div>
    );
  }

  return (
    <div className={`flex items-center gap-3 ${className}`}>
      <span className="font-serif text-2xl text-[#2c201b]">{firstName} {lastName}</span>
      <button
        onClick={() => setEditing(true)}
        className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-[#8f7e69]/20 bg-[#f6efe5] text-[#5a465f] transition-colors hover:bg-[#efe5d6] hover:text-[#2c201b] focus:outline-none"
        aria-label="Edit name"
      >
        <AiOutlineEdit className="text-lg" />
      </button>
    </div>
  );
};

export default EditableName;
